import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { StatCard } from "@/components/data-display/stat-card";
import { cn } from "@/lib/utils";
import type { WalletDetail } from "@/types";

const ASSET_TONE: Record<string, string> = {
  ETH: "var(--ads-asset-eth)",
  USDC: "var(--ads-asset-usdc)",
  USDT: "var(--ads-asset-usdt)",
};

const CROSS_MODULE_HINT = "Available once the linked module is built";

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 overflow-hidden rounded-lg border border-line bg-surface">
      <h2 className="border-b border-line px-5 pb-3.5 pt-[18px] text-[15px] font-bold text-ink">{title}</h2>
      {children}
    </section>
  );
}

function DisabledLink({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <span aria-disabled title={title} className="font-semibold text-primary">
      {children}
    </span>
  );
}

export default function WalletDetailView({ detail }: { detail: WalletDetail }) {
  return (
    <>
      <Breadcrumb items={[{ label: "Wallets", href: "/wallets" }, { label: detail.name }]} />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {detail.stats.map((s) => (
          <StatCard key={s.label} value={s.value} label={s.label} hint={s.hint} positiveHint={s.positiveHint} tone={s.tone} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_300px]">
        <div className="min-w-0">
          <Panel title="Balances">
            <table className="w-full border-collapse">
              <tbody>
                {detail.balances.map((b, i) => (
                  <tr key={b.symbol} className={cn(i < detail.balances.length - 1 && "border-b border-line")}>
                    <td className="px-5 py-3.5 align-middle">
                      <div className="flex items-center gap-2.5">
                        <span
                          aria-hidden
                          className="grid size-7 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white"
                          style={{ background: ASSET_TONE[b.symbol] ?? "var(--ads-muted)" }}
                        >
                          {b.iconLabel}
                        </span>
                        <div>
                          <div className="text-sm font-medium text-ink">{b.name}</div>
                          <div className="text-[13px] text-muted">{b.amount}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-right text-sm font-semibold text-ink">{b.usd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel title="Route Capabilities">
            <div className="grid grid-cols-1 sm:grid-cols-2">
              {detail.routes.map((r, i) => (
                <div
                  key={r.title}
                  className={cn(
                    "border-line px-5 py-4",
                    i % 2 === 0 && "sm:border-r",
                    i < detail.routes.length - (detail.routes.length % 2 === 0 ? 2 : 1) && "border-b"
                  )}
                >
                  <div className="text-sm font-semibold text-ink">{r.title}</div>
                  <div className={cn("text-[13px] font-semibold", r.supported ? "text-success" : "text-danger")}>
                    {r.supported ? "✓ Supported" : "✗ Not Available"}
                  </div>
                  <div className="mt-1 text-xs text-muted">{r.detail}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Recent Transfers">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-line bg-bg text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                    <th className="px-5 py-2.5">ID</th>
                    <th className="px-5 py-2.5">Direction</th>
                    <th className="px-5 py-2.5">Asset</th>
                    <th className="px-5 py-2.5">Amount</th>
                    <th className="px-5 py-2.5">Status</th>
                    <th className="px-5 py-2.5">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.recentTransfers.map((t, i) => (
                    <tr key={t.id} className={cn(i < detail.recentTransfers.length - 1 && "border-b border-line")}>
                      <td className="px-5 py-3 text-[13px]">
                        <Link to={`/transfers/${t.id}`} className="font-semibold text-primary hover:underline">
                          {t.id}
                        </Link>
                      </td>
                      <td className={cn("px-5 py-3 text-[13px]", t.direction === "sent" ? "text-danger" : "text-success")}>
                        {t.direction === "sent" ? "↑ Sent" : "↓ Received"}
                      </td>
                      <td className="px-5 py-3 text-[13px] text-ink">{t.asset}</td>
                      <td className="px-5 py-3 text-[13px] text-ink">{t.amount}</td>
                      <td className="px-5 py-3">
                        <Badge variant={t.statusVariant} dot={false} size="sm">
                          {t.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-[13px] text-muted">{t.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        <div className="min-w-0">
          <Panel title="Wallet Properties">
            <table className="w-full border-collapse text-[13px]">
              <tbody>
                {detail.properties.map((p, i) => (
                  <tr key={p.k} className={cn(i < detail.properties.length - 1 && "border-b border-line")}>
                    <td className="w-[45%] px-5 py-2.5 align-top text-muted">{p.k}</td>
                    <td className="px-5 py-2.5 text-right font-medium text-ink">
                      {p.k === "Policy" ? (
                        <Link to={`/governance/${p.v}`} className="font-semibold text-primary hover:underline">
                          {p.v}
                        </Link>
                      ) : (
                        p.v
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel title="Evidence">
            <div className="px-5 py-3.5">
              {detail.evidence.map((e) => (
                <div key={e.id} className="mb-2 text-[13px] last:mb-0">
                  <Link to={`/evidence/${e.id}`} className="font-semibold text-primary hover:underline">
                    {e.label}
                  </Link>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Key Recovery">
            <div className="px-5 py-3.5 text-[13px] font-medium">
              <DisabledLink title={CROSS_MODULE_HINT}>View recovery posture →</DisabledLink>
            </div>
          </Panel>

          <div className="mt-4">
            <Link
              to="/wallets"
              className="text-[13px] font-semibold text-primary transition-colors hover:underline"
            >
              ← Back to Wallets
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
